import { supabase } from './supabase';

// A teacher's subscription. The row is teacher_billing
// (20261027_teacher_billing.sql); Stripe keeps it current through the
// stripe-webhook function. This file only reads it and asks the billing
// function for Stripe pages.

export type Billing = {
  teacher_id: string;
  trial_ends_at: string;
  comped: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  status: string | null;
  plan: 'monthly' | 'yearly' | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
};

export type Plan = 'monthly' | 'yearly';

/** What the plans cost. The real prices live in Stripe; keep these in step. */
export const PRICES: Record<Plan, { amount: string; per: string; note: string }> = {
  monthly: { amount: '$10', per: 'month', note: 'Cancel any time.' },
  yearly: { amount: '$90', per: 'year', note: 'The price of nine months, so summer is free.' },
};

const LIVE = ['active', 'trialing', 'past_due'];

/** Has a Stripe subscription that is running (or retrying a card). */
export const isSubscribed = (b: Billing | null) => !!b && LIVE.includes(b.status ?? '');

/** Mirrors teacher_has_access() in SQL: may make classes and assignments. */
export function hasAccess(b: Billing | null, now = Date.now()): boolean {
  if (!b) return false;
  return b.comped || new Date(b.trial_ends_at).getTime() > now || isSubscribed(b);
}

/** Whole days left in the free trial (0 once it's over). */
export function trialDaysLeft(b: Billing | null, now = Date.now()): number {
  if (!b) return 0;
  return Math.max(0, Math.ceil((new Date(b.trial_ends_at).getTime() - now) / 86_400_000));
}

/** The signed-in teacher's row, starting their free trial the first time. */
export async function fetchBilling(): Promise<Billing | null> {
  const { data, error } = await supabase.rpc('start_teacher_trial');
  if (error) throw new Error(error.message);
  return (data as Billing | null)?.teacher_id ? (data as Billing) : null;
}

async function billingUrl(body: Record<string, unknown>): Promise<string> {
  const { data, error } = await supabase.functions.invoke('billing', { body });
  if (error) {
    let message = 'Could not reach the payment page. Try again.';
    try {
      const b = await (error as { context?: Response }).context?.json();
      if (b?.error) message = b.error;
    } catch { /* keep generic */ }
    throw new Error(message);
  }
  return (data as { url: string }).url;
}

/** Off to Stripe Checkout to subscribe. */
export async function startCheckout(plan: Plan): Promise<void> {
  window.location.assign(await billingUrl({ action: 'checkout', plan }));
}

/** Off to the Stripe customer portal: card, plan, invoices, cancel. */
export async function openPortal(): Promise<void> {
  window.location.assign(await billingUrl({ action: 'portal' }));
}
